import type { ReactNode } from 'react';
import { useTranslations } from '@/i18n/translations';
import { Button, I, Row } from '@leanin/ui';

export function PostActions({
  comments,
  commentsOpen,
  onComment,
  onSave,
  saved,
  reactSlot,
  shareSlot,
  extraSlot,
}: {
  comments?: number | string;
  commentsOpen?: boolean;
  onComment?: () => void;
  onSave?: () => void;
  saved?: boolean;
  /** The reaction control — the host renders ReactionButton (and its
   *  ReactionSummary) here. */
  reactSlot: ReactNode;
  /** Slot for a share trigger (typically `<ShareEntityButton entity={{kind:'post', id}} variant="ghost" size="sm" />`).
   *  Sits in the trailing icon cluster alongside save. Omit on view-only
   *  listings that have no real post identity to share. */
  shareSlot?: ReactNode;
  /** Extra trailing actions rendered before the share trigger — e.g. the
   *  resource page's Download PDF button. */
  extraSlot?: ReactNode;
}) {
  const t = useTranslations('Feed');
  const commentCountNum =
    typeof comments === 'number'
      ? comments
      : typeof comments === 'string'
        ? Number.parseInt(comments, 10) || 0
        : 0;

  return (
    <Row
      py="md"
      px="xl"
      align="center"
      justify="space-between"
      wrap
      style={{ borderTop: '1px solid var(--border)' }}
    >
      <Row gap="sm" align="center">
        {reactSlot}
        <Button
          variant="ghost"
          size="sm"
          icon={I.chat}
          onClick={onComment}
          // Tighter than the sm tier: icon + number reads loose at the
          // label-text padding. The reaction chip and summary match it.
          padX="sm"
          aria-expanded={commentsOpen}
          aria-label={t('actions.comment')}
        >
          {t('actions.commentCount', { count: commentCountNum })}
        </Button>
      </Row>

      <Row gap="xs" align="center">
        {extraSlot}
        {shareSlot}
        {onSave !== undefined ? (
          <Button
            variant="ghost"
            size="sm"
            icon={saved === true ? I.bookmarkFilled : I.bookmark}
            onClick={onSave}
            style={saved === true ? { color: 'var(--primary)' } : undefined}
            aria-pressed={saved === true}
            aria-label={saved === true ? t('actions.savedAria') : t('actions.saveAria')}
          />
        ) : null}
      </Row>
    </Row>
  );
}
